import { PriceLabel, PriceLabelSimple } from '@/components/features/shared/utils';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { SegmentedControl, SegmentedControlItem } from '@/components/ui/segmented-control';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Collector } from '@/domain/collectors/collector';
import { BillingType } from '@/domain/invoices/invoice';
import { PaymentMethod } from '@/domain/payments/payments';
import { Paths } from '@/routes/paths';
import { convertToDigitStr } from '@/utils/helpers';
import { useNavigate } from '@tanstack/react-router';
import { AlertCircle, ArrowLeft, FileText, Loader2, ReceiptText } from 'lucide-react';
import { useEffect } from 'react';
import { UseFormReturn } from 'react-hook-form';
import * as z from 'zod';

export const paymentFormSchema = z.object({
  paymentMethod: z.enum([
    PaymentMethod.CREDIT_CARD,
    PaymentMethod.DEBIT_CARD,
    PaymentMethod.BANK_TRANSFER,
    PaymentMethod.CASH,
    PaymentMethod.OTHER
  ] as any),
  billingType: z.enum([BillingType.Invoice, BillingType.Receipt] as any),
  paymentDetails: z.string().optional(),
  paymentVerified: z.boolean()
    .refine(val => {
      const method = (document.querySelector('input[name="paymentMethod"]:checked') as HTMLInputElement)?.value;
      return !['bank_transfer', 'cash', 'other'].includes(method) || val === true;
    }, 'Debe verificar que el pago ha sido recibido'),
  partialPayment: z.boolean(),
  amount: z.number().optional(),
  interestAmount: z.number().optional(),
  collectorId: z.string().optional().nullable(),
});

export type PaymentFormValues = z.infer<typeof paymentFormSchema>;

export interface PaymentAccreditationFormProps {
  form: UseFormReturn<PaymentFormValues>;
  onSubmit: (values: PaymentFormValues) => void;
  isPending: boolean;
  partialPaymentsEnabled: boolean;
  maxPartialAmount: number;
  amountToInvoice: number;
  amountToCharge: number;
  activeCollectors: Collector[];
  assignedCollectorName?: string | null;
  selectedDifferentCollector: boolean;
  amountOwed?: number;
  contractId?: string;
}

export const PaymentAccreditationForm = ({
  form,
  onSubmit,
  isPending,
  partialPaymentsEnabled,
  maxPartialAmount,
  amountToInvoice,
  amountToCharge,
  activeCollectors,
  assignedCollectorName,
  selectedDifferentCollector,
  amountOwed = 0,
  contractId,
}: PaymentAccreditationFormProps) => {
  const navigate = useNavigate();

  const goBackToContract = () => {
    if (contractId) {
      navigate({ to: `${Paths.Management.Contracts}/${contractId}` });
    }
  };
  const isPartialPayment = form.watch('partialPayment');
  const amount = form.watch('amount');
  const billingType = form.watch('billingType');
  const selectedPaymentMethod = form.watch('paymentMethod');
  const selectedCollectorId = form.watch('collectorId');

  const isCardPayment = selectedPaymentMethod === PaymentMethod.CREDIT_CARD || selectedPaymentMethod === PaymentMethod.DEBIT_CARD;
  const isVerifiedPayment = !isCardPayment ? form.watch('paymentVerified') : true;
  const hasCouponCode = isCardPayment ? !!form.watch('paymentDetails') : true;
  const hasValidAmount = isPartialPayment ? (amount && amount > 0 && amount < maxPartialAmount) : true;
  const hasInputErrors = !isVerifiedPayment || !hasCouponCode || !hasValidAmount;

  useEffect(() => {
    if (selectedPaymentMethod === PaymentMethod.OTHER) {
      // form.setValue('paymentVerified', true, { shouldValidate: true });
    }
    if (selectedCollectorId) {
      form.setValue('paymentMethod', PaymentMethod.CASH, { shouldValidate: true });
      // form.setValue('paymentVerified', true, { shouldValidate: true });
    }
  }, [selectedPaymentMethod, selectedCollectorId, form]);

  useEffect(() => {
    form.setValue('paymentVerified', true);
  }, [])

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        {partialPaymentsEnabled && (
          <div className='flex items-center gap-10 bg-gray-50 p-4 rounded-lg border border-gray-100'>
            {amountOwed > 0 && (
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-1">Saldo a abonar</h3>
                <PriceLabel price={amountOwed} />
              </div>
            )}
            <FormField
              control={form.control}
              name="partialPayment"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      className="mt-1"
                    />
                  </FormControl>
                  <div className="flex flex-col gap-1">
                    <FormLabel className="font-semibold text-gray-900">Pago parcial</FormLabel>
                    <FormDescription className="text-xs">
                      Marque esta casilla si el cliente realiza un pago parcial
                    </FormDescription>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        )}

        {isPartialPayment && (
          <div className='flex flex-col sm:flex-row gap-5 sm:gap-10 p-5 bg-blue-50/50 border border-blue-100 rounded-lg'>
            <FormField
              control={form.control}
              name="amount"
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormLabel className="text-blue-900 font-semibold">Monto del pago parcial</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      step="1"
                      min="0"
                      max={maxPartialAmount}
                      placeholder="Ingrese el monto"
                      value={field.value || ''}
                      onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : undefined)}
                      onWheel={(e) => (e.target as HTMLInputElement).blur()}
                      className="bg-white border-blue-200 focus-visible:ring-blue-500"
                    />
                  </FormControl>
                  <FormDescription className="text-blue-700/70 text-xs">
                    Monto menor a ${convertToDigitStr(maxPartialAmount)}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            {amount && amount > 0 && (
              <div className="space-y-1 sm:mt-0 mt-2">
                <p className="text-xs font-medium text-blue-800">Recibo a generar por:</p>
                <div className="text-xl font-bold text-blue-900">
                  <PriceLabel price={amount} />
                </div>
              </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="space-y-6">
            <FormField
              control={form.control}
              name="billingType"
              render={({ field }) => (
                <FormItem className="">
                  <FormLabel className="text-gray-700 font-semibold">Facturación</FormLabel>
                  <div className="flex flex-col gap-2">
                    <FormControl>
                      <SegmentedControl
                        value={field.value}
                        onValueChange={(value) => form.setValue('billingType', value as BillingType)}
                        disabled={isPartialPayment}
                      >
                        <SegmentedControlItem value={BillingType.Invoice}>
                          <FileText className="h-4 w-4" />
                          Factura
                        </SegmentedControlItem>
                        <SegmentedControlItem value={BillingType.Receipt}>
                          <ReceiptText className="h-4 w-4" />
                          Recibo
                        </SegmentedControlItem>
                      </SegmentedControl>
                    </FormControl>
                    {isPartialPayment && (
                      <p className="text-xs text-amber-600 font-medium">
                        Los pagos parciales se emiten como recibos.
                      </p>
                    )}
                  </div>
                </FormItem>
              )}
            />

            <Alert className='bg-blue-50/30 border-blue-100 text-blue-900'>
              <AlertCircle className="h-4 w-4 flex-shrink-0 text-blue-600" />
              <AlertTitle className="text-sm font-semibold text-blue-800">Emisión de documento</AlertTitle>
              <AlertDescription className="text-xs text-blue-700 mt-1">
                Se generará
                <strong>{billingType === BillingType.Invoice ? ' una factura ' : ' un recibo '}</strong>
                por
                <strong> ${convertToDigitStr(amountToInvoice.toString())}</strong>.
              </AlertDescription>
            </Alert>
          </div>

          <div className="space-y-6 flex flex-col justify-between">
            <FormField
              control={form.control}
              name="collectorId"
              render={({ field }) => (
                <FormItem className="">
                  <FormLabel className="text-gray-700 font-semibold">Asignar Pago al cobrador</FormLabel>
                  <Select
                    onValueChange={(val) => {
                      if (val !== '__none__') {
                        form.setValue('paymentVerified', true);
                      }
                      field.onChange(val === '__none__' ? null : val);
                    }}
                    value={field.value || '__none__'}
                  >
                    <FormControl>
                      <SelectTrigger className="bg-white">
                        <SelectValue placeholder="Ningún cobrador" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="__none__">Ningún cobrador</SelectItem>
                      {activeCollectors.map((collector: Collector) => (
                        <SelectItem key={collector.id} value={collector.id}>
                          {collector.code} - {collector.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {selectedDifferentCollector && assignedCollectorName && (
              <Alert variant="warning" className="bg-amber-50 border-amber-200">
                <AlertCircle className="h-4 w-4 text-amber-600" />
                <AlertTitle className="text-sm font-semibold text-amber-800">Cobrador diferente</AlertTitle>
                <AlertDescription className="text-xs text-amber-700 mt-1">
                  Este contrato está asignado al cobrador <strong>{assignedCollectorName}</strong>, pero se está registrando con un cobrador diferente.
                </AlertDescription>
              </Alert>
            )}

            <FormField
              control={form.control}
              name="paymentMethod"
              render={({ field }) => {
                const selectedPaymentMethod = form.watch('paymentMethod');
                const selectedCollectorId = form.watch('collectorId');

                return (
                  <FormItem className="">
                    <FormLabel className="text-gray-700 font-semibold">Método de pago</FormLabel>
                    <Select value={selectedPaymentMethod} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger className="bg-white">
                          <SelectValue placeholder="Seleccione método" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value={PaymentMethod.CREDIT_CARD}>Tarjeta de crédito</SelectItem>
                        <SelectItem value={PaymentMethod.DEBIT_CARD}>Tarjeta de débito</SelectItem>
                        <SelectItem value={PaymentMethod.BANK_TRANSFER}>Transferencia bancaria</SelectItem>
                        <SelectItem value={PaymentMethod.CASH}>Efectivo</SelectItem>
                        <SelectItem value={PaymentMethod.OTHER}>Otro</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                );
              }}
            />

            {(selectedPaymentMethod === PaymentMethod.CREDIT_CARD || selectedPaymentMethod === PaymentMethod.DEBIT_CARD) &&
              <Alert variant="warning" className="bg-amber-50 border-amber-200">
                <AlertCircle className="h-4 w-4 text-amber-600" />
                <AlertTitle className="text-sm font-semibold text-amber-800">Terminal Física</AlertTitle>
                <AlertDescription className="text-xs text-amber-700 mt-1">
                  Utilice la terminal y luego ingrese el número de cupón.
                </AlertDescription>
              </Alert>
            }

            {/* {(selectedPaymentMethod === PaymentMethod.BANK_TRANSFER || selectedPaymentMethod === PaymentMethod.CASH || selectedPaymentMethod === PaymentMethod.OTHER) && (
              <FormField
                control={form.control}
                name="paymentVerified"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border border-green-200 bg-green-50/50 p-4">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        className="mt-1 data-[state=checked]:bg-green-600 data-[state=checked]:text-white border-green-600"
                      />
                    </FormControl>
                    <div className="flex flex-col gap-1">
                      <FormLabel className="text-green-900 font-semibold">Confirmar recepción</FormLabel>
                      <FormDescription className="text-xs text-green-700">
                        Confirmo que el pago ha sido recibido correctamente.
                      </FormDescription>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )} */}
          </div>

          {(selectedPaymentMethod === PaymentMethod.CREDIT_CARD || selectedPaymentMethod === PaymentMethod.DEBIT_CARD) && (
            <FormField
              control={form.control}
              name="paymentDetails"
              render={({ field }) => (
                <FormItem className="">
                  <FormLabel className="text-gray-700 font-semibold">Número de cupón*</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Ej: 123456"
                      className="font-mono bg-white"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          {selectedPaymentMethod === PaymentMethod.CREDIT_CARD && (
            <FormField
              control={form.control}
              name="interestAmount"
              render={({ field }) => (
                <FormItem className="">
                  <FormLabel className="text-gray-700 font-semibold">Interés tarjeta</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="Ej: 1500"
                      className="bg-white"
                      value={field.value || ''}
                      onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : undefined)}
                      onWheel={(e) => (e.target as HTMLInputElement).blur()}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
        </div>

        {hasInputErrors && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-md flex items-center">
            <AlertCircle className="mr-2 h-4 w-4 text-red-600" />
            <span className="text-sm font-medium text-red-800">
              {!isVerifiedPayment ? 'Debe verificar que el pago ha sido recibido' :
                !hasCouponCode ? 'Debe ingresar el número de cupón para pagos con tarjeta' :
                  !hasValidAmount ? 'El monto parcial debe ser mayor a 0 y menor al monto a abonar' :
                    'Complete todos los campos requeridos'}
            </span>
          </div>
        )}

        <div className="pt-4 border-t flex flex-col sm:flex-row justify-between gap-3">
          {contractId && (
            <Button
              type="button"
              variant="outline"
              onClick={goBackToContract}
              className="w-full sm:w-auto font-medium"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Volver al contrato
            </Button>
          )}
          <Button
            type="submit"
            variant={"info"}
            size="lg"
            className="w-full sm:w-auto font-semibold px-8 shadow-sm"
            disabled={hasInputErrors || isPending}
          >
            {isPending && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            {billingType === BillingType.Invoice ? <FileText className="h-4 w-4" /> : <ReceiptText className="h-4 w-4" />}
            {billingType === BillingType.Invoice ? "Emitir factura" : "Emitir recibo"} por <PriceLabelSimple price={amountToCharge} />
          </Button>
        </div>
      </form>
    </Form>
  );
};
